import { useState, useEffect } from 'react'
import { useMutation, useQuery, useApolloClient } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  FormControlLabel,
  Checkbox,
  Link,
  Divider,
} from '@mui/material'

import { queries, mutations } from '../../graphql'
import { useAuth } from '../../hooks'
import { LinearProgress } from '../../components'

const TERMS_VERSION = '1.0'

export function ClaimFundExistingUser({ token, claimDetails, onSwitchToNewUser }) {
  const navigate = useNavigate()
  const client = useApolloClient()
  const [{ isLoggedIn }, { login: setAuthToken }] = useAuth()
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [termsError, setTermsError] = useState(null)
  const [justLoggedIn, setJustLoggedIn] = useState(false)

  // Get current user info to check if they need to accept terms
  const { data: userData, loading: loadingUser } = useQuery(queries.user.me, {
    skip: !isLoggedIn,
  })

  const [loginData, setLoginData] = useState({
    email: claimDetails.clientEmail,
    password: '',
  })
  const [loginError, setLoginError] = useState(null)
  const [loggingIn, setLoggingIn] = useState(false)

  const [declineError, setDeclineError] = useState(null)
  const [declining, setDeclining] = useState(false)

  const [claimFund, { loading, error }] = useMutation(mutations.fundClaims.claimFundExistingUser, {
    refetchQueries: [{ query: queries.user.me }],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      navigate(`/claim-fund/${token}/success`, {
        state: { otherPendingInvites: claimDetails.otherPendingInvites, fundId: data.claimFundExistingUser.id },
      })
    },
    onError: (err) => {
      console.error('Claim fund error:', err)
    },
  })

  const [declineInvite] = useMutation(mutations.fundClaims.declineFundClaimInvite, {
    onCompleted: () => {
      navigate('/funds')
    },
    onError: (err) => {
      setDeclineError(err.message)
      setDeclining(false)
    },
  })

  const handleDecline = async () => {
    setDeclineError(null)
    setDeclining(true)
    await declineInvite({ variables: { token } })
  }

  const [loginMutation] = useMutation(mutations.auth.login, {
    onCompleted: async ({ login: { access_token: authToken } = {} }) => {
      await setAuthToken(authToken)
      await client.refetchQueries({ include: [{ query: queries.user.me }] })
      setJustLoggedIn(true)
    },
    onError: (err) => {
      setLoginError(err.networkError?.result?.message || err.message || 'Login failed')
    },
  })

  const needsTermsAcceptance = claimDetails.requiresTermsAcceptance

  // After login completes, automatically trigger claim
  useEffect(() => {
    if (justLoggedIn && isLoggedIn) {
      // Give time for queries to refetch
      setJustLoggedIn(false)
    }
  }, [justLoggedIn, isLoggedIn])

  const handleClaimFund = async () => {
    if (needsTermsAcceptance && !acceptTerms) {
      setTermsError('You must accept the Terms & Conditions')
      return
    }

    await claimFund({
      variables: {
        token,
        acceptTerms: needsTermsAcceptance ? acceptTerms : undefined,
        termsVersion: needsTermsAcceptance ? TERMS_VERSION : undefined,
      },
    })
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoginError(null)
    setLoggingIn(true)

    try {
      await loginMutation({
        variables: {
          email: loginData.email,
          password: loginData.password,
        },
      })
    } catch (err) {
      setLoginError(err.message || 'Login failed')
    } finally {
      setLoggingIn(false)
    }
  }

  if (loadingUser) {
    return <LinearProgress />
  }

  // If logged in, show claim confirmation
  if (isLoggedIn) {
    const currentUser = userData?.me

    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Financial Advisor Access Request
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {claimDetails.advisorName} has requested to advise on your Giving Fund
            </Typography>
          </Box>

          <Box sx={{ mb: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {claimDetails.fundType === 'corporate'
                ? claimDetails.corporateName || claimDetails.fundName
                : `${claimDetails.clientFirstName} ${claimDetails.clientLastName}`}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error.message}
            </Alert>
          )}

          {declineError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {declineError}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 3 }}>
            You are signed in as <strong>{currentUser?.email}</strong>
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            By accepting, {claimDetails.advisorName} will have limited access to your fund and can assist you with
            contributions and gifts. You can modify their access from within your Giving Fund profile.
          </Typography>

          {needsTermsAcceptance && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body1" sx={{ mb: 2 }}>
                Please accept our Terms & Conditions to continue:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptTerms}
                    onChange={(e) => {
                      setAcceptTerms(e.target.checked)
                      setTermsError(null)
                    }}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link
                      href="https://guide.givewise.ca/policy-and-user-documents/terms-and-conditions"
                      target="_blank"
                      rel="noopener"
                    >
                      Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link
                      href="https://guide.givewise.ca/policy-and-user-documents/privacy-policy"
                      target="_blank"
                      rel="noopener"
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
              {termsError && (
                <Typography color="error" variant="caption" display="block" sx={{ ml: 4 }}>
                  {termsError}
                </Typography>
              )}
            </>
          )}

          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={loading || declining}
            onClick={handleClaimFund}
            sx={{ mt: 3 }}
          >
            {loading ? 'Approving...' : 'Approve Financial Advisor Access'}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            fullWidth
            size="large"
            disabled={loading || declining}
            onClick={handleDecline}
            sx={{ mt: 2 }}
          >
            {declining ? 'Declining...' : 'Decline'}
          </Button>
        </Paper>
      </Container>
    )
  }

  // Not logged in - show login form
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Financial Advisor Access Request
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {claimDetails.advisorName} has requested to advise on your Giving Fund
          </Typography>
        </Box>

        <Box sx={{ mb: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {claimDetails.fundName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            User Name: {claimDetails.clientFirstName} {claimDetails.clientLastName}
          </Typography>
        </Box>

        {loginError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loginError}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }}>
          Please sign in to review this request.
        </Alert>

        <form onSubmit={handleLogin}>
          <TextField
            fullWidth
            label="Email"
            value={loginData.email}
            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            type="password"
            label="Password"
            value={loginData.password}
            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
            sx={{ mb: 2 }}
            required
          />

          <Link href="/forgot-password" variant="body2" sx={{ display: 'block', mb: 3 }}>
            Forgot your password?
          </Link>

          <Button type="submit" variant="contained" color="primary" fullWidth size="large" disabled={loggingIn}>
            {loggingIn ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Don&apos;t have an account?
          </Typography>
          <Button variant="outlined" color="primary" size="small" onClick={onSwitchToNewUser}>
            Sign Up
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}
